/* eslint-disable max-len */
import { ApprovalStatusLabel } from '../../enumerations';
import {
  handleUrl,
  myHeadersWithToken,
  myHeadersWithTokenContentType,
} from '../../global';
import {
  DetailResponse,
  DiscreditCompanyProps,
  EditLicensedCompanyProps,
  GetCompanyAbsencesResponse,
  GetCompanyNoticesQueries,
  GetCompanyNoticesResponse,
  GetCompanyResponse,
  GetLicensedCompanyResponse,
  GetObservationsResponse,
  LicenseCompanyProps,
  LicensedCompanyResponse,
  ListCompaniesResponse,
  ObservationProps,
  OperationFlowResponse,
} from './types';

export const getCompany = (doc: string): Promise<GetCompanyResponse> => {
  const requestOptions = {
    method: 'GET',
    headers: myHeadersWithToken(),
  };
  const baseUrl = handleUrl();
  return fetch(`${baseUrl}/api/V1/companies/document/${doc}`, requestOptions)
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const licenseCompany = (
  data: LicenseCompanyProps
): Promise<LicensedCompanyResponse> => {
  const requestOptions = {
    method: 'POST',
    headers: myHeadersWithTokenContentType(),
    body: JSON.stringify(data),
  };
  const baseUrl = handleUrl();
  return fetch(`${baseUrl}/api/V1/licensed-companies`, requestOptions)
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const listLicensedCompanies = (
  page: number,
  size: number,
  search: string,
  cityId?: number,
  isActive?: boolean
): Promise<ListCompaniesResponse> => {
  const baseUrl = handleUrl();
  const url = new URL(
    `${baseUrl}/api/V1/licensed-companies?page=${page}&size=${size}`
  );
  if (search !== '') url.searchParams.append('search', search);
  if (cityId !== undefined)
    url.searchParams.append('city_id', cityId.toString());
  if (isActive !== undefined)
    url.searchParams.append('is_active', isActive.toString());

  const requestOptions = {
    method: 'GET',
    headers: myHeadersWithTokenContentType(),
  };
  return fetch(url, requestOptions)
    .then((response) => response.json())
    .catch((error) => new Error(error));
};

export const getLicensedCompany = (
  id: number
): Promise<GetLicensedCompanyResponse> => {
  const requestOptions = {
    method: 'GET',
    headers: myHeadersWithTokenContentType(),
  };
  const baseUrl = handleUrl();
  return fetch(`${baseUrl}/api/V1/licensed-companies/${id}`, requestOptions)
    .then((response) => response.json())
    .catch((error) => new Error(error));
};

export const editLicensedCompany = (
  id: number,
  data: EditLicensedCompanyProps
): Promise<DetailResponse> => {
  const requestOptions = {
    method: 'PATCH',
    headers: myHeadersWithTokenContentType(),
    body: JSON.stringify(data),
  };
  const baseUrl = handleUrl();
  return fetch(`${baseUrl}/api/V1/licensed-companies/${id}`, requestOptions)
    .then((response) => response.json())
    .catch((error) => new Error(error));
};

export const discreditLicensedCompany = ({
  noticeId,
  licensedCompanyId,
  data,
}: DiscreditCompanyProps): Promise<DetailResponse> => {
  const requestOptions = {
    method: 'PATCH',
    headers: myHeadersWithTokenContentType(),
    body: JSON.stringify(data),
  };
  const baseUrl = handleUrl();
  return fetch(
    `${baseUrl}/api/V1/public-notices/${noticeId}/licensed-companies/${licensedCompanyId}/discredit`,
    requestOptions
  )
    .then((response) => response.json())
    .catch((error) => new Error(error));
};

export const addObservation = (
  company: number,
  data: ObservationProps
): Promise<DetailResponse> => {
  const requestOptions = {
    method: 'POST',
    headers: myHeadersWithTokenContentType(),
    body: JSON.stringify(data),
  };
  const baseUrl = handleUrl();
  return fetch(
    `${baseUrl}/api/V1/licensed-companies/${company}/observations`,
    requestOptions
  )
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const getObservations = (
  company: number,
  page: number,
  size: number
): Promise<GetObservationsResponse> => {
  const requestOptions = {
    method: 'GET',
    headers: myHeadersWithTokenContentType(),
  };
  const baseUrl = handleUrl();
  return fetch(
    `${baseUrl}/api/V1/licensed-companies/${company}/observations?page=${page}&size=${size}`,
    requestOptions
  )
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const getCompanyNotices = (
  company: number,
  page: number,
  size: number,
  query: GetCompanyNoticesQueries = {}
): Promise<GetCompanyNoticesResponse> => {
  const requestOptions = {
    method: 'GET',
    headers: myHeadersWithTokenContentType(),
  };

  const params = new URLSearchParams({
    page: String(page),
    size: String(size),
    ...query,
  });
  const baseUrl = handleUrl();
  return fetch(
    `${baseUrl}/api/V1/licensed-companies/${company}/public-notices?${params.toString()}`,
    requestOptions
  )
    .then((response) => response.json())
    .catch((error) => new Error(error));
};

export const getCompanyAbsences = (
  company: number,
  page: number,
  size: number
): Promise<GetCompanyAbsencesResponse> => {
  const requestOptions = {
    method: 'GET',
    headers: myHeadersWithTokenContentType(),
  };
  const baseUrl = handleUrl();
  return fetch(
    `${baseUrl}/api/V1/licensed-companies/${company}/absences?page=${page}&size=${size}`,
    requestOptions
  )
    .then((response) => response.json())
    .catch((error) => new Error(error));
};

export const getOperationFlow = (
  publicNoticeId: number,
  page: number,
  size: number,
  approvalStatus: ApprovalStatusLabel[],
  search?: string
): Promise<OperationFlowResponse> => {
  const requestOptions = {
    method: 'GET',
    headers: myHeadersWithTokenContentType(),
  };

  const params = new URLSearchParams({
    page: String(page),
    size: String(size),
  });
  approvalStatus.forEach((status) => params.append('approval_status', status));
  if (search) params.append('search', search);

  const baseUrl = handleUrl();
  return fetch(
    `${baseUrl}/api/V1/public-notices/${publicNoticeId}/licensed-companies/operation-flow?${params.toString()}`,
    requestOptions
  )
    .then((response) => response.json())
    .catch((error) => new Error(error));
};
